import DOMPurify from 'isomorphic-dompurify'
import { allPass, flatten, propEq } from 'ramda'
import {
  BenchmarkStatusReason,
  CtaDefinition,
  DetailRequirement,
  EngagementArtifact,
  EngagementDisplayStatus,
  EngagementStatusBlock,
  EngagementUser,
  ProgramEngagement,
  ProgramEngagementStatus,
  ProgramEngagementUser,
  ProgramLedger,
  ProgramStageType,
  ProgramStep,
  ProgramTask,
  ReplacementTokenMap,
  TaskStatusReason,
  UserId,
} from '../types'
import { evaluateTemplateExpression, TokenContext } from '../utils/evaluateTemplateExpression'

export function tokensByAddress(
  obj: ReplacementTokenMap,
  prefix = '',
  res: Record<string, string> = {}
) {
  for (const [key, value] of Object.entries(obj)) {
    const newKey = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      tokensByAddress(value, newKey, res)
    } else {
      res[newKey] = value as string
    }
  }

  return res
}

export class ProgramEngagementModel implements ProgramEngagement {
  id: string
  programId: string
  displayActor: 'participant' | 'manager' | 'managerAdmin' | 'ciboSupport'
  artifacts: EngagementArtifact[]
  displayStatus: EngagementDisplayStatus
  ctaEngageElt?: CtaDefinition
  ctaPath?: CtaDefinition[]
  createdBy: ProgramEngagementUser
  currentStep: string
  currentStepType: ProgramStageType
  newFieldStep?: string
  fieldCounts?: { total: number } | undefined
  fields: string[]
  name?: string | undefined
  ownedBy: ProgramEngagementUser
  space?: 'account' | 'user' | undefined
  status: ProgramEngagementStatus[]
  availableStageStatuses: ProgramEngagementStatus[]

  userId: UserId
  updatedAt: string
  updatedBy: ProgramEngagementUser
  overallStatus?: EngagementStatusBlock
  relevantUserInfo?: Record<'advisor', EngagementUser>
  wfTags?: string[]

  copyReplacementTokens: ReplacementTokenMap
  copyTokenMap: Record<string, string>

  constructor(engagement: ProgramEngagement) {
    this.displayActor = engagement.displayActor
    this.artifacts = engagement.artifacts
    this.createdBy = engagement.createdBy
    this.displayStatus = engagement.displayStatus
    this.currentStep = engagement.currentStep
    this.newFieldStep = engagement.newFieldStep
    this.currentStepType = engagement.currentStepType
    this.fieldCounts = engagement.fieldCounts
    this.fields = engagement.fields
    this.id = engagement.id
    this.name = engagement.name
    this.ownedBy = engagement.ownedBy
    this.programId = engagement.programId
    this.relevantUserInfo = engagement.relevantUserInfo
    this.space = engagement.space
    this.status = engagement.status
    this.availableStageStatuses =
      engagement.status?.filter(stage => stage.available !== false) || []
    this.userId = engagement.userId
    this.updatedAt = engagement.updatedAt
    this.updatedBy = engagement.updatedBy
    this.overallStatus = engagement.overallStatus
    this.wfTags = engagement.wfTags

    this.copyReplacementTokens = engagement.copyReplacementTokens
    this.copyTokenMap =
      engagement.copyReplacementTokens && tokensByAddress(engagement.copyReplacementTokens)
  }

  get ownerDisplayName() {
    return this.ownedBy.givenName
      ? `${this.ownedBy.givenName} ${this.ownedBy.familyName}`
      : this.ownedBy.userPrimaryEmail
  }

  findArtifact(slot: string) {
    return this.artifacts.find(a => a.slot === slot)
  }

  get currentStepId() {
    return this.currentStep?.split(':')?.[1]
  }

  /*
   * Each program will have a single `entrance` task that covers the benchmark for adding a field to an
   * engagement. It is possible to add a field which meets the benchmark to an engagement, but then the
   * field's data changes and no longer meets the benchmark. In this case, we want to show the user that
   * the field is no longer eligible for the program.
   *
   * This function returns the status of the `entrance` task.
   */
  entranceTaskStatus() {
    return this.getTaskStatus('entranceTask')
  }

  firstNonValidStepAndTask() {
    const firstNonValidStep = findNodeByComparator({
      comparator: (status: ProgramEngagementStatus) =>
        status.type === 'step' && !status.status.valid,
      status: this.status,
    })

    if (!firstNonValidStep) return

    const firstNonValidTask = firstNonValidStep.children?.find(
      t => t.status?.valid === false && t.status?.progress?.completed
    )

    const stage = parentNode({
      id: firstNonValidStep.id,
      root: { id: 'root', children: this.status },
    })

    return { stage, step: firstNonValidStep, task: firstNonValidTask }
  }

  /**
   * The first incomplete task in the given step, excluding the tasks with the filterIds.
   */
  firstVisibleIncompleteTask({
    stepId,
    hiddenViaContentful: filterIds = [],
  }: { stepId?: ProgramStep['id']; hiddenViaContentful?: string[] } = {}) {
    const step = this.getStepStatus(stepId)

    if (!step) return

    const candidateTasks = step.children
      ?.filter(({ id }) => filterIds.includes(id) === false)
      .filter(task => {
        const taskStatus = this.getTaskStatus(task.id)
        if (taskStatus?.available === false) return false

        const taskEditorParams = taskStatus?.displays?.find(({ type }) => type === 'taskEditor')
          ?.params

        return !taskEditorParams?.modal
      })

    return candidateTasks?.find(t => t.status?.progress?.completed === false)
  }

  firstIncompleteStep() {
    return findNodeByComparator({
      comparator: (status: ProgramEngagementStatus) =>
        status.type === 'step' && status.status.progress?.completed === false,
      status: this.status,
    })
  }

  firstFailedTask(stepId: ProgramStep['id']) {
    const step = this.getStepStatus(stepId)
    if (!step) return
    return step.children?.find(t => !t.status?.eligibility?.eligible)
  }

  get currentStage() {
    return this.status.find(s => s.type === 'stage' && s.status?.progress?.completed === false)
  }

  getStatus(props: { id: string }) {
    return findNode({ ...props, status: this.status })
  }

  getTaskStatus(id?: string) {
    return id ? findNode({ id, status: this.status }) : undefined
  }

  getStepStatus(id?: string) {
    return id ? findNode({ id, status: this.status }) : undefined
  }

  getStageStatus(id: string) {
    return findNode({ id, status: this.status })
  }

  ineligibleTaskFieldReasons(taskId: string) {
    const task = this.getTaskStatus(taskId)
    return (
      task?.status?.eligibility?.reasonItems?.filter(({ dataType }) => dataType === 'field') ?? []
    )
  }

  ineligibleTaskEngagementReasons(taskId: string) {
    const task = this.getTaskStatus(taskId)
    return (
      task?.status?.eligibility?.reasonItems?.filter(({ dataType }) => dataType === 'workflow') ??
      []
    )
  }

  ineligibleTaskUserReasons(taskId: string) {
    const task = this.getTaskStatus(taskId)
    return (
      task?.status?.eligibility?.reasonItems?.filter(({ dataType }) => dataType === 'user') ?? []
    )
  }

  isFailedTask(id: string) {
    const task = this.getTaskStatus(id)
    return task?.status?.valid === false && task?.status?.progress?.completed
  }

  exceedsProgramGrowerLimits({ ledger, acres }: { ledger: ProgramLedger; acres: number }) {
    return (
      (ledger.maxAcresPerGrower && acres > ledger.maxAcresPerGrower) ||
      (ledger.maxFieldsPerGrower && this.fields.length > ledger.maxFieldsPerGrower)
    )
  }

  isComplete() {
    return (
      this.overallStatus?.eligibility?.eligible &&
      this.overallStatus?.valid &&
      this.overallStatus?.warnings?.noWarnings &&
      this.overallStatus?.progress?.completed
    )
  }

  geometryIneligbilityReasons() {
    return (
      this.overallStatus?.eligibility?.eligible === false &&
      !!this.overallStatus?.eligibility?.reasonItems.find(a =>
        a.reasons.find(b => b.traitId === 'geometry')
      ) &&
      this.overallStatus?.eligibility?.reasonItems
    )
  }

  // @todo: prefer backend status with https://cibotech.atlassian.net/browse/CPD-5899
  systemWideStatus() {
    if (this.isComplete()) return 'completed'

    const currentStage = this.currentStage

    if (!currentStage) return 'unknown'

    return ['enroll', 'apply'].includes(currentStage.id) ? 'inProgress' : 'active'
  }

  lastStepAndStage() {
    const lastStage = this.status?.filter(a => a.type === 'stage').slice(-1)[0]
    const lastStep = lastStage?.children?.filter(a => a.type === 'step').slice(-1)[0]
    return { stage: lastStage, step: lastStep }
  }

  taskIsMissingFieldInfo(taskId: string, fieldId: string, requirement: DetailRequirement) {
    const taskStatus = this.getTaskStatus(taskId)

    const fieldProgressStatus = taskStatus?.status.progress?.reasonItems.find(
      allPass([propEq('dataType', 'field'), propEq('id', fieldId)])
    )

    return fieldProgressStatus?.reasons?.some(
      allPass([
        propEq('reason', 'missingData'),
        reason => reason['traitId'] === requirement.traitId,
        reason => !requirement.year || reason['year'] === requirement.year,
      ])
    )
  }

  warningsForTask(taskId: string) {
    const task = this.getTaskStatus(taskId)
    return task?.status?.warnings?.reasonItems ?? []
  }

  taskHasWarning(taskId: string, warningReason: string) {
    const task = this.getTaskStatus(taskId)
    return task?.status?.warnings?.reasonItems?.some(item =>
      item.reasons?.some(reason => reason.reason === warningReason)
    )
  }

  stepDescendentIneligibilityReasons(stepId?: string) {
    if (!stepId) return []
    const status = this.getStepStatus(stepId)
    const allIneligibleNodes =
      !!status &&
      findNodesByComparator({
        comparator: a => !a.status.valid && !!a.status.progress && a.status.progress.completed,
        status: status,
      })
    return !!allIneligibleNodes
      ? (flatten(allIneligibleNodes.map(status => status.status.eligibility?.reasonItems)).filter(
          Boolean
        ) as TaskStatusReason<BenchmarkStatusReason>[])
      : []
  }

  nextAvailableStepId(id: ProgramStep['id']) {
    for (const stage of this.availableStageStatuses) {
      const parent = parentNode({ id, root: stage })
      if (parent) {
        const stepIndex = parent.children?.findIndex(propEq('id', id))

        if (stepIndex !== undefined) {
          const remainingSteps = parent.children
            ?.slice(stepIndex + 1)
            .filter((step: ProgramEngagementStatus) => step.available !== false)

          if (remainingSteps.length > 0) {
            return remainingSteps[0].id
          }
        }
      }
    }
  }

  nextAvailableStepAndTask({
    stepId,
    taskId,
  }: {
    taskId?: ProgramTask['id'] | 'status'
    stepId?: ProgramStep['id']
  }) {
    if (!!taskId) {
      for (const stage of this.availableStageStatuses) {
        const parent = parentNode({ id: taskId, root: stage })
        if (parent) {
          const taskIndex = parent.children?.findIndex(propEq('id', taskId))

          if (taskIndex !== undefined) {
            const remainingTasks = parent.children
              .slice(taskIndex + 1)
              .filter((task: ProgramEngagementStatus) => task.available !== false)

            if (remainingTasks.length > 0) {
              return {
                nextStepId: parent.id,
                nextTaskId: remainingTasks[0].id,
              }
            }
          }
        }
      }
    }
    if (!!stepId) {
      for (const stage of this.availableStageStatuses) {
        const parent = parentNode<ProgramStep>({ id: stepId, root: stage })
        if (parent) {
          const stepIndex = parent.children?.findIndex(propEq('id', stepId))

          if (stepIndex !== undefined) {
            const remainingSteps = parent.children
              .slice(stepIndex + 1)
              .filter((step: ProgramEngagementStatus) => step.available !== false)

            if (remainingSteps.length > 0) {
              return {
                nextStepId: remainingSteps[0].id,
                nextTaskId: remainingSteps[0].children.filter(
                  (task: ProgramEngagementStatus) => task.available !== false
                )[0]?.id,
              }
            }
          }
        }
      }
    }
    // NOTE: an 'undefined' next task return results in navigation to the 'status' page
    // in the `navigateToNextTask` hook, which is a desired behavior.
    return undefined
  }

  replaceCopyTokens(template?: string, tokenContext?: TokenContext): string {
    if (!template) return ''

    const tokens = this.copyTokenMap

    // Step 1: Replace {{copyTokens}}
    let resolved = template
    if (tokens && Object.keys(tokens).length > 0) {
      resolved = resolved.replace(/{{(.*?)}}/g, (_, key) => {
        return key in tokens
          ? DOMPurify.sanitize(tokens[key], { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
          : `{{${key}}}` // fallback to original
      })
    }

    // Step 2: Evaluate ${cel} template expressions
    if (tokenContext) {
      const result = evaluateTemplateExpression(resolved, tokenContext)
      resolved = typeof result === 'string' ? result : String(result)
    }

    return resolved
  }
}

const findNode = ({
  id,
  status,
}: {
  id: string
  status: ProgramEngagementStatus[]
}): ProgramEngagementStatus | undefined => {
  if (!status) return
  const hit = status.find(s => s.id === id)
  if (hit) return hit
  for (const s of status) {
    if (s.children) {
      const childHit = findNode({ id, status: s.children })
      if (childHit) return childHit
    }
  }
}

const findNodeByComparator = ({
  comparator,
  status,
}: {
  comparator: (status: ProgramEngagementStatus) => boolean
  status: ProgramEngagementStatus[]
}): ProgramEngagementStatus | undefined => {
  if (!status) return
  const hit = status.find(comparator)
  if (hit) return hit
  for (const s of status) {
    if (s.children) {
      const childHit = findNodeByComparator({ comparator, status: s.children })
      if (childHit) return childHit
    }
  }
}

const findNodesByComparator = ({
  comparator,
  status,
}: {
  comparator: (status: ProgramEngagementStatus) => boolean
  status: ProgramEngagementStatus
}): ProgramEngagementStatus[] | undefined => {
  const hits = []
  const hit = status.children?.filter(comparator)
  if (hit) hits.push(...hit)
  for (const s of status.children) {
    if (s.children) {
      const childHits = findNodesByComparator({ comparator, status: s })
      if (childHits) hits.push(...childHits)
    }
  }
  return hits.filter(Boolean).length > 0 ? flatten(hits) : undefined
}

type EngagementStatusNode = {
  id: string
  children?: EngagementStatusNode[]
}

//@ts-ignore TODO: why is this function type complaining when findNode is not????
export const parentNode = <T = EngagementStatusNode>({
  id,
  root,
}: {
  id: string
  root: EngagementStatusNode
}) => {
  if (root.children?.find(propEq('id', id))) {
    return root as T
  }

  let result: T | undefined
  for (const child of root.children ?? []) {
    result = parentNode<T>({ id, root: child })
    if (result) return result
  }
}
